// sensei-app/client/ui/CAdminCourseCrud.js
import { ComponentBase } from "../../core/ComponentBase.js";

export class CAdminCourseCrud extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataCrud = null;
        this.courseAdminId = null;
        // The eventGroup is now derived from the instanceId or a default, making it more robust.
        this.eventGroup = `${this.config.instanceId || 'cadminCourseCrud'}-crud`;
    }

    async initialize() {
        await super.initialize();
        // The component will wait for initAfterLogin to be called by ComponentBase
        // after login and permission checks are complete.
    }

    async initAfterLogin() {
        // If this method is called, we can assume the user has permission.
        this.courseAdminId = this.appClient.getSharedData('userId');
        await this.setupDataCrud();

        if (this.dataCrud) {
            // The event name is now correctly namespaced to this component instance.
            this.dataCrud.dataView.on(`manageDocuments`, this.handleManageDocuments.bind(this));
            await this.dataCrud.refreshData();
        } else {
            this.log.error("DataCrud component failed to initialize within CAdminCourseCrud.");
        }

        // If the component was already rendered (showing a loading state),
        // this will re-render it with the fully initialized DataCrud component.
        if (this.container) {
            await this.render();
        }
    }

    async setupDataCrud() {
        if (this.dataCrud) return;

        this.dataCrud = await this.createManagedComponent("DataCrud", null, {
            eventGroup: this.eventGroup, // Use the unique event group
            rpcClass: "CAdminCourseRpc",
            schemaPath: "collections/courses",
            title: "Course Management",
            viewConfig: {
                mode: 'grid',
                fields: [
                    { name: 'title', label: 'Course Title', render: (val) => `<strong>${val}</strong>` },
                    { name: 'isPublished', label: 'Published', render: (val) => val ? '<i class="bi bi-check-circle-fill text-success"></i> Yes' : '<i class="bi bi-x-circle text-muted"></i> No' },
                    { name: 'topics', label: 'Topics', render: (val) => `<span class="badge bg-secondary">${val?.length || 0}</span>` },
                    { name: 'passingScore', label: 'Passing Score', render: (val) => val != null ? val : 'N/A' },
                ],
                actions: [
                    {
                        label: 'Manage Content',
                        icon: 'bi-file-earmark-text-fill',
                        className: 'btn-outline-info btn-sm',
                        eventName: 'manageDocuments' // This will be prefixed by the eventGroup
                    }
                ],
            },
            formConfig: {
                mode: 'edit',
                fields: {
                    title: { type: 'text', label: 'Course Title' },
                    description: { type: 'textarea', label: 'Description', rows: 3 },
                    isPublished: { type: 'boolean', label: 'Publish this course' },
                    passingScore: { type: 'number', label: 'Passing Score', description: 'Minimum total score required to pass the course.' },
                    topics: {
                        type: 'array',
                        label: 'Course Curriculum',
                        item: {
                            fields: {
                                title: { type: 'text', label: 'Topic Title' },
                                type: {
                                    type: 'select',
                                    label: 'Type',
                                    options: [
                                        { value: 'class', label: 'Class' },
                                        { value: 'assessment', label: 'Assessment' },
                                        { value: 'assignment', label: 'Assignment' }
                                    ]
                                },
                                documentPath: { type: 'text', label: 'Content Document Path', description: 'e.g., "Topic 1 - Introduction.md"' },
                                marks: { type: 'number', label: 'Total Marks' },
                                isMandatory: { type: 'boolean', label: 'Mandatory for Completion', default: true }
                            }
                        }
                    }
                },
                sections: [
                    { title: "Course Details", fields: ["title", "description", "isPublished", "passingScore"] },
                    { title: "Curriculum Topics", fields: ["topics"] }
                ]
            },
            dataConfig: { autoLoad: false, pageSize: 20 },
            defaultValues: {
                courseAdminId: this.courseAdminId
            }
        });
    }

    handleManageDocuments(item) {
        if (item && item._id) {
            this.log.info(`Navigating to manage documents for course: ${item.title} (CourseID: ${item._id})`);
            // Navigate to the documentation manager, providing the course's documentInstanceId as the context.
            // The CAdminDocumentationManager will use this to load the correct set of documents.
            this.appClient.layoutEngine.navigate(`/course-admin/course-documents?contextId=${item._id}&contextType=course-content`);
        } else {
            this.appClient.notifier.warn("Cannot manage documents: Course ID or Document Instance ID is missing.");
            this.log.warn("Manage Documents clicked but item or item._id/documentInstanceId is missing:", item);
        }
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'cadmin-course-crud-container h-100';
        }
        this.container.innerHTML = '';

        // Simplified rendering logic. If dataCrud is initialized, render it.
        // Otherwise, show a loading message. This state is temporary until initAfterLogin completes.
        if (this.dataCrud) {
            this.container.appendChild(await this.dataCrud.render());
        } else {
            const loadingMsg = document.createElement('div');
            loadingMsg.className = 'p-3 text-muted text-center';
            loadingMsg.textContent = this.t('Loading Course Manager...');
            this.container.appendChild(loadingMsg);
        }

        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    destroy() {
        if (this.dataCrud) {
            // Unsubscribe from the specific event
            this.dataCrud.destroy();
            this.dataCrud = null;
        }
        super.destroy();
    }
}